import { useEffect, useRef } from "react";
import i18n from "i18next";
import { authIntegration, notificationIntegration } from "@/lib/api/client";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useNotificationWebSocket } from "@/hooks/useNotificationWebSocket";
import { useReferenceSync } from "@/hooks/useReferenceSync.js";
import { useAuthStore } from "@/store/auth.store.js";
import { useFcmForegroundNotifications } from "@/hooks/useFcmForegroundNotifications";
import { requestFcmToken } from "@/lib/firebaseMessaging";

export function useAppInit() {
    useWebSocket();
    useNotificationWebSocket();
    useReferenceSync();

    useFcmForegroundNotifications();
    
    useEffect(() => {
        const updateLang = () => {
            document.documentElement.lang = i18n.language || "en";
        };
        updateLang();
        i18n.on('languageChanged', updateLang);
        return () => {
            i18n.off('languageChanged', updateLang);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const runCheckAuth = () => {
            if (cancelled || useAuthStore.getState().isLoggingOut) return;
            authIntegration.checkAuth();
        };

        if (useAuthStore.persist?.hasHydrated?.()) {
            runCheckAuth();
            return () => {
                cancelled = true;
            };
        }

        const unsubHydration = useAuthStore.persist?.onFinishHydration?.(() => {
            runCheckAuth();
        });

        return () => {
            cancelled = true;
            if (typeof unsubHydration === 'function') {
                unsubHydration();
            }
        };
    }, []);

    const fcmRequestedRef = useRef(false);

    useEffect(() => {
        if (fcmRequestedRef.current) return;
        fcmRequestedRef.current = true;

        let isMounted = true;

        (async () => {
            const token = await requestFcmToken();
            if (!isMounted || !token) return;

            await notificationIntegration.registerPushToken(token);
        })();

        return () => {
            isMounted = false;
        };
    }, []);
}
